import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Asterisk } from "@phosphor-icons/react";

const CLIENTS = [
    { name: "Rapid Fleet Management Services Limited", logo: "/logos/rapidfleet.png" },
    { name: "Surat Goods Transport Pvt Ltd", logo: "/logos/suratgoods.png" },
    { name: "Aqualite Industries Pvt Ltd", logo: "/logos/aqualite.png" },
    { name: "Caravan Multimodal Movers Pvt Ltd", logo: "/logos/caravan.jpg" },
    { name: "Drishti Global Private Limited", logo: "/logos/drishti-new.png" },
    { name: "Rohlig India Private Limited", logo: "/logos/rohlig.png" },
    { name: "Everfast Freight Forwarders Pvt Ltd", logo: "/logos/everfast.webp" },
];

export default function Clients() {
    return (
        <section id="clients" className="border-t border-white/10" data-testid="clients-section">
            <div className="px-5 pt-24 md:px-10 md:pt-32">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-14 flex flex-wrap items-end justify-between gap-6 md:mb-20"
                >
                    <div>
                        <p className="mb-4 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-blaze md:text-xs">
                            <span className="inline-block h-2 w-2 bg-blaze" />
                            Trusted By
                        </p>
                        <h2 className="font-display text-4xl uppercase leading-[0.95] tracking-tight md:text-6xl">
                            Clients who move <span className="text-outline-faint">with us.</span>
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm leading-relaxed text-white/50 md:text-base">
                        Fleet operators, manufacturers and forwarders who trust the Akash network with their cargo, trip after trip.
                    </p>
                </motion.div>
            </div>

            <div className="border-y border-white/10 py-5" data-testid="clients-marquee">
                <Marquee speed={24} gradient={false} pauseOnHover>
                    {CLIENTS.map((client) => (
                        <span key={client.name} className="mx-8 flex items-center gap-16">
                            <span className="whitespace-nowrap font-display text-2xl uppercase tracking-wide text-white/50 md:text-3xl">
                                {client.name}
                            </span>
                            <Asterisk size={24} weight="bold" className="shrink-0 text-blaze/70" />
                        </span>
                    ))}
                </Marquee>
            </div>

            <div className="grid grid-cols-2 gap-px bg-white/10 lg:grid-cols-4" data-testid="clients-grid">
                {CLIENTS.map((client, i) => (
                    <motion.div
                        key={client.name}
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                        className="group relative flex aspect-[16/10] flex-col justify-between bg-void p-5 transition-colors duration-300 hover:bg-panel md:p-7"
                        data-testid={`client-card-${i + 1}`}
                    >
                        <div className="flex items-start justify-between">
                            <span className="text-[10px] uppercase tracking-[0.25em] text-white/25 transition-colors duration-300 group-hover:text-blaze">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="h-1.5 w-1.5 bg-white/15 transition-colors duration-300 group-hover:bg-blaze" />
                        </div>
                        <div className="flex flex-1 items-center justify-center px-2">
                            {client.logo ? (
                                <span className="flex items-center justify-center border border-white/10 bg-[#FFFDF8] px-5 py-3 shadow-sm transition-transform duration-300 group-hover:scale-105">
                                    <img
                                        src={client.logo}
                                        alt={`${client.name} logo`}
                                        loading="lazy"
                                        className="max-h-12 w-auto max-w-full object-contain md:max-h-16"
                                    />
                                </span>
                            ) : (
                                <p className="text-center font-display text-base uppercase leading-snug tracking-wide text-white/70 transition-colors duration-300 group-hover:text-white md:text-xl">
                                    {client.name}
                                </p>
                            )}
                        </div>
                        <span className="pointer-events-none absolute bottom-0 left-0 h-0.5 w-0 bg-blaze transition-[width] duration-500 group-hover:w-full" />
                    </motion.div>
                ))}

                <motion.a
                    href="#enquiry"
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="group relative flex aspect-[16/10] flex-col items-center justify-center gap-3 bg-blaze p-5 transition-colors duration-300 hover:bg-white md:p-7"
                    data-testid="clients-cta-card"
                >
                    <p className="text-center font-display text-xl uppercase leading-snug tracking-wide text-white transition-colors duration-300 group-hover:text-void md:text-2xl">
                        Your cargo here?
                    </p>
                    <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/80 transition-colors duration-300 group-hover:text-void">
                        Become a client
                        <ArrowUpRight size={14} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                </motion.a>
            </div>
        </section>
    );
}
